import { ComponentProps } from "react";
import { ImagePresets } from "@pages/Article/components/ArticleThumbnail/utils/enums";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import { cn } from "@util/helpers";

type TemplateIProps = {
  /** The list of card props */
  cardProps: ComponentProps<typeof Card>[];
  /** Additional class name for the container */
  className?: string;
};

/**
 * Template I component for Singapore Budget, BTSG60 Top Stories
 * Figma: https://www.figma.com/design/FZiuxiCLQaNXAJmLRtAAY9/BUDGET-2025--WEB---APP---NL-?node-id=8518-386590&t=NrMkoRPajprK1OWW-4
 */
const TemplateI = ({ cardProps, className }: TemplateIProps) => {
  const mainVariation: CardVariations = {
    image: {
      position: "left",
      width: "hidden md:block md:w-1/2",
      aspectRatio: "aspect-3x2",
      imageClassName: "object-cover md:flex",
      orientation: "landscape",
      filters: { w: ImagePresets.MEDIUM, dpr: 1, f: "webp" },
    },
    content: {
      width: "w-1/2",
      extraClass: cn("bg-gray-850 text-center md:ml-4 md:bg-transparent"),
      blurb: {
        extraClass:
          "hidden md:block font-public-sans text-md mt-2 text-gray-850 text-left",
      },
      updatedTimeColor: cn("text-gray-850"),
      updatedTimeExtraClass: cn("font-poppins text-7xs"),
      updatedTimeWrapperClass: cn("pt-2 lg:mt-auto"),
    },
    title: {
      color: "text-white md:text-gray-850",
      size: "text-4xl",
      extraClass: cn(
        "p-4 text-center font-lct md:text-left leading-[1.8125rem] md:p-0 md:text-left lg:text-gray-850"
      ),
    },
    kicker: {
      color: cn("text-white lg:text-black"),
      extraClass: cn("font-poppins text-4xs font-light leading-6"),
    },
  };

  const otherVariation: CardVariations = {
    title: {
      color: "text-gray-850",
      size: "text-base lg:text-lg",
      extraClass: cn("font-bold font-lct"),
    },
    content: {
      width: "w-full",
      updatedTimeColor: cn("text-gray-850"),
      updatedTimeExtraClass: cn("font-poppins text-7xs"),
      updatedTimeWrapperClass: cn("pt-2 lg:mt-auto"),
    },
    kicker: {
      color: "text-gray-550",
      extraClass: "mb-2",
    },
  };

  return (
    <div
      data-testid="singapore-budget-top-stories-component"
      className={cn("mb-10 md:grid md:grid-cols-3 xl:grid-cols-2", className)}
    >
      {cardProps.map((card, index) => {
        return (
          <div
            className={cn("col-span-1 border-b border-gray-175 py-4", {
              "col-span-3 border-b-0 md:border-b xl:col-span-2": index === 0,
              "md:border-b-0": index > 3,
              "xl:border-b-0":
                index === cardProps.length - 1 ||
                index === cardProps.length - 2,
              "xl:border-b": index === 4,
              "border-b-0 pb-0 md:pb-4": index === 6,
            })}
            key={`budget-top-stories-${card.id}`}
          >
            <Card
              {...card}
              variations={index === 0 ? mainVariation : otherVariation}
              className={cn("h-full border-gray-175", {
                "md:border-r md:pr-3 xl:border-r-0 xl:pr-0": [
                  1, 2, 4, 5,
                ].includes(index),
                "md:mr-3 md:pl-3 xl:mr-0 xl:pl-0": [2, 5].includes(index),
                "xl:border-r xl:pr-3": [1, 3, 5].includes(index),
                "xl:pl-3": [2, 4, 6].includes(index),
              })}
            />
          </div>
        );
      })}
    </div>
  );
};

export default TemplateI;
